import React, { createContext, useEffect, useState, useCallback } from 'react';

export const RecaptchaContext = createContext(null);

const RECAPTCHA_SCRIPT_ID = 'recaptcha-v3-script';

const loadRecaptchaScript = (siteKey, onReady, onError) => {
    // Check if script already loaded
    if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(onReady);
        return;
    }

    // Check if script is already being loaded
    if (document.getElementById(RECAPTCHA_SCRIPT_ID)) {
        // Wait for it to load
        const checkReady = setInterval(() => {
            if (window.grecaptcha && window.grecaptcha.ready) {
                clearInterval(checkReady);
                window.grecaptcha.ready(onReady);
            }
        }, 100);
        return;
    }

    // Load the reCAPTCHA v3 script dynamically
    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
        if (window.grecaptcha && window.grecaptcha.ready) {
            window.grecaptcha.ready(onReady);
        }
    };

    script.onerror = onError;

    document.head.appendChild(script);
};

const RecaptchaProvider = ({ children }) => {
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState(null);
    const [siteKey, setSiteKey] = useState(null);

    useEffect(() => {
        // Fetch client config from backend to get reCAPTCHA site key
        const fetchSiteKey = async () => {
            try {
                const baseUrl = (window.GIFTSTARR_CFG && window.GIFTSTARR_CFG.API_URL) || '/api';
                const response = await fetch(`${baseUrl}/clientConfig`);
                if (!response.ok) {
                    console.log('Failed to fetch client config, proceeding without reCAPTCHA');
                    return;
                }
                const config = await response.json();
                const configSiteKey = config.recaptcha_site_key;

                if (!configSiteKey) {
                    console.log('reCAPTCHA site key not configured, proceeding without reCAPTCHA');
                    return;
                }

                setSiteKey(configSiteKey);

                loadRecaptchaScript(
                    configSiteKey,
                    () => setIsReady(true),
                    () => {
                        console.error('Failed to load reCAPTCHA script');
                        setError(new Error('Failed to load reCAPTCHA script'));
                    }
                );
            } catch (err) {
                console.log('Error fetching client config:', err);
                // Proceed without reCAPTCHA
            }
        };

        fetchSiteKey();
    }, []);

    const executeRecaptcha = useCallback(async (action) => {
        if (!isReady || !siteKey || !window.grecaptcha) {
            // reCAPTCHA not available - return null to indicate it should be skipped
            return null;
        }

        try {
            const token = await window.grecaptcha.execute(siteKey, { action });
            return token;
        } catch (err) {
            console.error('reCAPTCHA execution error:', err);
            setError(err);
            // Return null instead of throwing - allow the request to proceed
            return null;
        }
    }, [isReady, siteKey]);

    const contextValue = {
        executeRecaptcha,
        isReady,
        error,
        isConfigured: !!siteKey
    };

    return (
        <RecaptchaContext.Provider value={contextValue}>
            {children}
        </RecaptchaContext.Provider>
    );
};

export default RecaptchaProvider;
